import { useState, type ReactNode } from 'react';
import {
  Avatar,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  Flex,
  Heading,
  InlineStack,
  Link,
  Paragraph,
  Search,
  Stack,
  Table,
  TableTbody,
  TableTh,
  TableThead,
  TableTr,
} from '@customerio/pluma-components/react';
import type { Relationship } from '@customerio/hydra-ui-api';
import { ObjectAttributeRow, useObjectAttributes } from '@/features/objects';
import type { IdentifiersConfig } from '@/features/people';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { customerName } from '@/services/db/customer/display';
import type { Customer } from '@/services/db/customer/entity';
import { isEmptyString } from '@/utils/string';
import { RelationshipAttributeList } from './relationship-attribute-list';
import { useRelationshipAttributesSave } from './use-relationship-attributes-save';

/** Ember's relationship model lists attributes as ordered one-key pairs. */
function attributePairs(attributes: Readonly<Record<string, string>>): Array<Record<string, string>> {
  return Object.entries(attributes).map(([key, value]) => ({ [key]: value }));
}

type PreviewPersonProps = {
  customer: Customer | undefined;
  personName: string | undefined;
  personHref: string | undefined;
};

/**
 * What Ember's InitializedAvatar shows for a person: the full name, else the
 * email's local part, unless Gravatar is on to stand in for it. The email
 * only goes to the avatar when Gravatar may fetch for it.
 */
export type PreviewAvatar = { name: string | undefined; email: string | undefined };

export function previewAvatar(customer: Customer | undefined, showGravatar: boolean): PreviewAvatar {
  if (customer == null) return { name: undefined, email: undefined };
  const email = customer.attributes.email ?? customer.identifiers.email;
  const fullName = customerName(customer.attributes);
  if (isEmptyString(email)) return { name: fullName ?? undefined, email: undefined };
  return {
    name: fullName ?? (showGravatar ? undefined : email.split('@')[0]),
    email: showGravatar ? email : undefined,
  };
}

/** The related person: avatar, name link, and email beneath it. */
function PreviewPerson({ customer, personName, personHref }: PreviewPersonProps) {
  const avatar = previewAvatar(customer, useFeatureFlag(AccountFeatureFlag.GravatarEnabled));
  const email = customer?.attributes.email ?? customer?.identifiers.email;

  return (
    <Flex as="section" alignItems="center">
      <Avatar size="xl" name={avatar.name} email={avatar.email} autoColor mr="200" />
      {personHref == null ? null : (
        <Link href={personHref} variant="secondary" isUnderlined={false} m="0" className="fs-mask">
          {personName}
          {isEmptyString(email) ? null : <Paragraph variant="secondary">{email}</Paragraph>}
        </Link>
      )}
    </Flex>
  );
}

type RelationshipPreviewDrawerProps = {
  workspaceId: number;
  cioObjectId: string;
  objectTypeId: string;
  relationship: Relationship;
  customer: Customer | undefined;
  personName: string | undefined;
  personHref: string | undefined;
  identifiers: IdentifiersConfig | null | undefined;
  /** Relationship attribute names this type has seen, offered while naming a new one. */
  knownAttributes: readonly string[];
  canEdit: boolean;
  onClose: () => void;
};

/**
 * One relationship's attributes beside the table: read them, filter them by
 * name, or switch to editing them in place. A relationship with none opens
 * straight into editing, since there is nothing to read.
 *
 * Remount it per relationship (`key`): the editor seeds itself once.
 */
export function RelationshipPreviewDrawer(props: RelationshipPreviewDrawerProps) {
  const { relationship, canEdit, onClose } = props;
  const [attributes, setAttributes] = useState(relationship.attributes);
  const pairs = attributePairs(attributes);
  const [isEditing, setIsEditing] = useState(pairs.length === 0);
  const [searchTerm, setSearchTerm] = useState('');
  // Bumped after each save, so the next edit starts from what was saved.
  const [editSession, setEditSession] = useState(0);

  const header = (
    <>
      <Box mb="400">
        <PreviewPerson customer={props.customer} personName={props.personName} personHref={props.personHref} />
      </Box>
      <InlineStack alignInline="space-between" mb="200">
        <Heading level="3" color="bold" mb="100">
          Relationship attributes
        </Heading>
        {isEditing ? null : (
          <Link
            icon="chevron-right"
            iconPosition="trailing"
            isUnderlined={false}
            isDisabled={!canEdit}
            onClick={() => setIsEditing(true)}
            data-test-manage-rel-attribute
          >
            Manage
          </Link>
        )}
      </InlineStack>
    </>
  );

  return (
    <Drawer isOpen onClose={onClose} title="Relationship preview" data-test-relationship-preview-drawer>
      {isEditing ? (
        <RelationshipAttributesEditor
          key={editSession}
          {...props}
          header={header}
          pairs={pairs}
          onSaved={(saved) => {
            setAttributes(saved);
            setEditSession((session) => session + 1);
            setSearchTerm('');
            setIsEditing(Object.keys(saved).length === 0);
          }}
          onCancel={() => (pairs.length === 0 ? onClose() : setIsEditing(false))}
        />
      ) : (
        <DrawerBody>
          {header}
          <RelationshipAttributesView
            pairs={pairs}
            searchTerm={searchTerm}
            identifiers={props.identifiers}
            onSearch={setSearchTerm}
          />
        </DrawerBody>
      )}
    </Drawer>
  );
}

type RelationshipAttributesViewProps = {
  pairs: ReadonlyArray<Record<string, string>>;
  searchTerm: string;
  identifiers: IdentifiersConfig | null | undefined;
  onSearch: (term: string) => void;
};

function RelationshipAttributesView({ pairs, searchTerm, identifiers, onSearch }: RelationshipAttributesViewProps) {
  const trimmed = searchTerm.trim();
  // Ember matches the key case-sensitively, as a substring.
  const matches = trimmed === '' ? pairs : pairs.filter((pair) => (Object.keys(pair)[0] ?? '').includes(trimmed));

  return (
    <Stack gap="200">
      <InlineStack>
        <Search
          size="md"
          value={searchTerm}
          placeholder="Find attribute by name"
          ariaLabel="Attribute name search"
          shouldSearchOnInput
          onSearchInput={onSearch}
          onSearch={onSearch}
          data-test-attributes-search
        />
      </InlineStack>
      {pairs.length > 0 && matches.length === 0 ? (
        <Heading level="3" as="div" color="subtle">
          No attributes for this person matching "{searchTerm}"
        </Heading>
      ) : (
        <RelationshipAttributeList attributes={matches} identifiers={identifiers} />
      )}
    </Stack>
  );
}

type RelationshipAttributesEditorProps = RelationshipPreviewDrawerProps & {
  header: ReactNode;
  pairs: ReadonlyArray<Record<string, string>>;
  onSaved: (attributes: Record<string, string>) => void;
  onCancel: () => void;
};

function RelationshipAttributesEditor(props: RelationshipAttributesEditorProps) {
  const editor = useObjectAttributes({ initialAttrs: props.pairs, isNew: false, hasReservedNames: false });
  const { save, isSaving } = useRelationshipAttributesSave({
    workspaceId: props.workspaceId,
    cioObjectId: props.cioObjectId,
    objectTypeId: props.objectTypeId,
    relationship: props.relationship,
    editor,
    onSaved: props.onSaved,
  });
  const inUse = new Set(editor.rows.map((row) => row.key));
  const suggestions = props.knownAttributes.filter((name) => !inUse.has(name));

  return (
    <>
      <DrawerBody>
        {props.header}
        <Table layout="fixed" caption="Edit relationship attributes" mb="100">
          <TableThead>
            <TableTr>
              <TableTh>Attribute name</TableTh>
              <TableTh>Attribute value</TableTh>
            </TableTr>
          </TableThead>
          <TableTbody>
            {editor.rows.map((row) => (
              <ObjectAttributeRow
                key={row.id}
                row={row}
                errors={editor.errorsFor(row)}
                isNew={false}
                isSaving={isSaving}
                showErrors={editor.isDirty}
                hasReservedNames={false}
                isMultiIdentifier={false}
                keySuggestions={suggestions}
                onKeyChange={editor.setKey}
                onValueChange={editor.setValue}
                onRemove={editor.remove}
              />
            ))}
          </TableTbody>
        </Table>
        <Box mt="200" mb="100">
          <Button
            variant="secondary"
            icon="add"
            iconPosition="leading"
            isDisabled={isSaving}
            onClick={() => editor.create()}
          >
            Create new attribute
          </Button>
        </Box>
      </DrawerBody>
      <DrawerFooter>
        <Button
          variant="secondary"
          onClick={() => {
            editor.reset();
            props.onCancel();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          // Ember gates Save on a change, on validity, and on the edit permission.
          isDisabled={!editor.isDirty || editor.isInvalid || !props.canEdit}
          isLoading={isSaving}
          onClick={save}
          data-test-save-attribute-button
        >
          Save
        </Button>
      </DrawerFooter>
    </>
  );
}
